import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Inbox, Send, Plus, X, Search, MoreVertical, Trash2, User, BookOpen } from 'lucide-react';
import { auth, db } from '../firebase';
import {
  collection, addDoc, updateDoc, deleteDoc, doc, getDocs, setDoc,
  serverTimestamp, query, orderBy, onSnapshot, arrayUnion
} from 'firebase/firestore';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';

// ──────────────────────────────────────────────
// Kebab / three-dot dropdown menu
// ──────────────────────────────────────────────
function KebabMenu({ onDeleteForMe, onDeleteForEveryone }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
        aria-label="Thread options"
      >
        <MoreVertical className="h-4 w-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-1 w-52 rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
          <button
            onClick={(e) => { e.stopPropagation(); setOpen(false); onDeleteForMe(); }}
            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete for me only
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setOpen(false); onDeleteForEveryone(); }}
            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete for everyone
          </button>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────
// Main component
// ──────────────────────────────────────────────
export default function MessagesTab({ onUnreadCount }) {
  const [threads, setThreads] = useState([]);
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [filterType, setFilterType] = useState('all');
  const messagesEndRef = useRef(null);

  // Delete confirmation modal state
  const [deleteModal, setDeleteModal] = useState({ open: false, threadId: null, mode: null }); // mode: 'forMe' | 'forEveryone'

  // Compose modal state
  const [composeOpen, setComposeOpen] = useState(false);
  const [recipientFilter, setRecipientFilter] = useState('all'); // 'all' | 'authors' | 'readers'
  const [recipientSearch, setRecipientSearch] = useState('');
  const [recipientResults, setRecipientResults] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState(null); // { id, name, email, type: 'author'|'reader' }
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [composeSending, setComposeSending] = useState(false);
  const [allAuthors, setAllAuthors] = useState([]);
  const [allReaders, setAllReaders] = useState([]);

  const currentUid = auth.currentUser?.uid;

  // ── Subscribe to adminMessages threads ──
  useEffect(() => {
    const q = query(collection(db, 'adminMessages'), orderBy('updatedAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      const items = [];
      snap.forEach(d => items.push({ id: d.id, ...d.data() }));
      setThreads(items);
      // Unread count excludes threads deleted for current user
      const uid = auth.currentUser?.uid;
      const visible = uid ? items.filter(t => !(t.deletedFor || []).includes(uid)) : items;
      const unread = visible.filter(t => t.unreadByAdmin).length;
      if (onUnreadCount) onUnreadCount(unread);
    }, (err) => {
      console.error('[CRM MessagesTab] adminMessages listener error:', err.code, err.message);
      setThreads([]);
      if (onUnreadCount) onUnreadCount(0);
    });
    return unsub;
  }, [onUnreadCount]);

  // ── Load messages for selected thread ──
  useEffect(() => {
    if (!selectedThreadId) { setThreadMessages([]); return; }
    const q = query(
      collection(db, 'adminMessages', selectedThreadId, 'messages'),
      orderBy('createdAt', 'asc')
    );
    const unsub = onSnapshot(q, (snap) => {
      const items = [];
      snap.forEach(d => items.push({ id: d.id, ...d.data() }));
      setThreadMessages(items);
    });
    return unsub;
  }, [selectedThreadId]);

  // ── Auto-scroll ──
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [threadMessages.length]);

  // ── Mark thread as read when selected ──
  useEffect(() => {
    if (!selectedThreadId) return;
    const thread = threads.find(t => t.id === selectedThreadId);
    if (thread?.unreadByAdmin) {
      updateDoc(doc(db, 'adminMessages', selectedThreadId), { unreadByAdmin: false }).catch(() => {});
    }
  }, [selectedThreadId, threads]);

  // ── Load authors and readers for compose modal ──
  useEffect(() => {
    if (!composeOpen) return;

    // Fetch authors
    const unsubAuthors = onSnapshot(collection(db, 'authors'), (snap) => {
      const items = [];
      snap.forEach(d => items.push({ id: d.id, ...d.data() }));
      setAllAuthors(items);
    });

    // Fetch readers
    const unsubReaders = onSnapshot(collection(db, 'readers'), (snap) => {
      const items = [];
      snap.forEach(d => items.push({ id: d.id, ...d.data() }));
      setAllReaders(items);
    });

    return () => { unsubAuthors(); unsubReaders(); };
  }, [composeOpen]);

  // ── Client-side search for recipients ──
  useEffect(() => {
    if (!composeOpen || !recipientSearch.trim()) {
      setRecipientResults([]);
      return;
    }
    const term = recipientSearch.toLowerCase();

    let results = [];

    if (recipientFilter === 'all' || recipientFilter === 'authors') {
      const authorMatches = allAuthors
        .filter(a => {
          const name = (a.displayName || a.name || a.penName || '').toLowerCase();
          const email = (a.email || '').toLowerCase();
          return name.includes(term) || email.includes(term);
        })
        .map(a => ({
          id: a.id,
          name: a.displayName || a.name || a.penName || 'Unnamed Author',
          email: a.email || '',
          type: 'author',
        }));
      results = results.concat(authorMatches);
    }

    if (recipientFilter === 'all' || recipientFilter === 'readers') {
      const readerMatches = allReaders
        .filter(r => {
          const name = (r.name || r.displayName || '').toLowerCase();
          const email = (r.email || '').toLowerCase();
          return name.includes(term) || email.includes(term);
        })
        .map(r => ({
          id: r.id,
          name: r.name || r.displayName || 'Unnamed Reader',
          email: r.email || '',
          type: 'reader',
        }));
      results = results.concat(readerMatches);
    }

    setRecipientResults(results.slice(0, 20));
  }, [recipientSearch, recipientFilter, allAuthors, allReaders, composeOpen]);

  // ── Filtered threads (type filter + deletedFor filter) ──
  const filteredThreads = threads.filter(t => {
    // Hide threads deleted for current user
    if (currentUid && (t.deletedFor || []).includes(currentUid)) return false;
    if (filterType === 'all') return true;
    return t.messageType === filterType;
  });

  const activeThread = selectedThreadId ? threads.find(t => t.id === selectedThreadId) : null;

  // ── Reply handler ──
  const handleReply = async () => {
    if (!selectedThreadId || !replyText.trim()) return;
    try {
      await addDoc(collection(db, 'adminMessages', selectedThreadId, 'messages'), {
        content: replyText.trim(),
        sender: 'admin',
        createdAt: serverTimestamp(),
      });
      await updateDoc(doc(db, 'adminMessages', selectedThreadId), {
        lastMessage: replyText.trim(),
        updatedAt: serverTimestamp(),
        unreadByAdmin: false,
        unreadByReader: true,
      });

      // Update reader's thread index
      if (activeThread?.readerId) {
        await updateDoc(
          doc(db, 'readers', activeThread.readerId, 'messageThreads', selectedThreadId),
          {
            lastMessage: replyText.trim(),
            updatedAt: serverTimestamp(),
            unreadByReader: true,
          }
        ).catch(() => {});
      }

      setReplyText('');
    } catch (err) {
      alert('Error sending reply: ' + err.message);
    }
  };

  // ── Delete for me only ──
  const handleDeleteForMe = async (threadId) => {
    if (!currentUid || !threadId) return;
    try {
      await updateDoc(doc(db, 'adminMessages', threadId), {
        deletedFor: arrayUnion(currentUid),
      });
      if (selectedThreadId === threadId) {
        setSelectedThreadId(null);
      }
    } catch (err) {
      alert('Error hiding thread: ' + err.message);
    }
  };

  // ── Delete for everyone (hard delete) ──
  const handleDeleteForEveryone = async (threadId) => {
    if (!threadId) return;
    try {
      const thread = threads.find(t => t.id === threadId);

      // 1. Delete all messages in subcollection
      const messagesSnap = await getDocs(collection(db, 'adminMessages', threadId, 'messages'));
      const deletePromises = [];
      messagesSnap.forEach(d => {
        deletePromises.push(deleteDoc(doc(db, 'adminMessages', threadId, 'messages', d.id)));
      });
      await Promise.all(deletePromises);

      // 2. Delete the reader's thread index
      if (thread?.readerId) {
        await deleteDoc(doc(db, 'readers', thread.readerId, 'messageThreads', threadId)).catch(() => {});
      }

      // 3. Delete the parent thread doc
      await deleteDoc(doc(db, 'adminMessages', threadId));

      if (selectedThreadId === threadId) {
        setSelectedThreadId(null);
      }
    } catch (err) {
      alert('Error deleting thread: ' + err.message);
    }
  };

  // ── Confirm delete action ──
  const confirmDelete = async () => {
    const { threadId, mode } = deleteModal;
    setDeleteModal({ open: false, threadId: null, mode: null });
    if (mode === 'forMe') {
      await handleDeleteForMe(threadId);
    } else if (mode === 'forEveryone') {
      await handleDeleteForEveryone(threadId);
    }
  };

  // ── Open delete modal helper ──
  const openDeleteModal = useCallback((threadId, mode) => {
    setDeleteModal({ open: true, threadId, mode });
  }, []);

  // ── Compose: send new message ──
  const handleComposeSend = async () => {
    if (!selectedRecipient || !composeBody.trim()) return;
    setComposeSending(true);

    try {
      const uid = auth.currentUser?.uid;
      const now = serverTimestamp();
      const subject = composeSubject.trim() || 'New message';

      if (selectedRecipient.type === 'author') {
        // Write to authors/{authorId}/messages/{threadId}
        const threadRef = doc(collection(db, 'authors', selectedRecipient.id, 'messages'));
        await setDoc(threadRef, {
          messageType: 'admin_direct',
          senderId: uid,
          senderRole: 'superadmin',
          recipientId: selectedRecipient.id,
          recipientName: selectedRecipient.name,
          recipientEmail: selectedRecipient.email,
          subject,
          lastMessage: composeBody.trim(),
          createdAt: now,
          updatedAt: now,
          unreadByRecipient: true,
        });

        // Add the first message to a messages subcollection
        await addDoc(collection(db, 'authors', selectedRecipient.id, 'messages', threadRef.id, 'messages'), {
          content: composeBody.trim(),
          sender: 'admin',
          senderRole: 'superadmin',
          createdAt: now,
        });
      } else {
        // Reader: write to adminMessages/{threadId}
        const threadData = {
          messageType: 'general',
          senderId: uid,
          senderRole: 'superadmin',
          readerId: selectedRecipient.id,
          readerName: selectedRecipient.name,
          readerEmail: selectedRecipient.email,
          subject,
          lastMessage: composeBody.trim(),
          createdAt: now,
          updatedAt: now,
          unreadByAdmin: false,
          unreadByReader: true,
        };
        const threadRef = await addDoc(collection(db, 'adminMessages'), threadData);

        // Add the first message
        await addDoc(collection(db, 'adminMessages', threadRef.id, 'messages'), {
          content: composeBody.trim(),
          sender: 'admin',
          senderRole: 'superadmin',
          createdAt: now,
        });

        // Dual-write reader thread index
        await setDoc(doc(db, 'readers', selectedRecipient.id, 'messageThreads', threadRef.id), {
          readerName: selectedRecipient.name,
          readerEmail: selectedRecipient.email,
          lastMessage: composeBody.trim(),
          updatedAt: now,
          unreadByReader: true,
          messageType: 'general',
        });
      }

      // Reset compose modal
      setComposeOpen(false);
      setSelectedRecipient(null);
      setRecipientSearch('');
      setComposeSubject('');
      setComposeBody('');
      setRecipientResults([]);
    } catch (err) {
      alert('Error sending message: ' + err.message);
    } finally {
      setComposeSending(false);
    }
  };

  const resetComposeModal = () => {
    setComposeOpen(false);
    setSelectedRecipient(null);
    setRecipientSearch('');
    setComposeSubject('');
    setComposeBody('');
    setRecipientResults([]);
    setRecipientFilter('all');
  };

  const dmUnreadTotal = filteredThreads.filter(t => t.unreadByAdmin).length;

  // ──────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────
  return (
    <div className="px-4 py-6 md:px-6">
      <div className="mx-auto max-w-5xl">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Platform Messages</h2>
            <p className="mt-1 text-sm text-slate-500">Support queries and direct messages</p>
          </div>
          <div className="flex items-center gap-2">
            {/* New Message button */}
            <Button
              onClick={() => setComposeOpen(true)}
              className="gap-1.5"
              size="sm"
            >
              <Plus className="h-4 w-4" />
              New Message
            </Button>

            {/* Filter */}
            <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-0.5">
              {[
                { id: 'all', label: 'All' },
                { id: 'general', label: 'General' },
                { id: 'author_related', label: 'Author-related' },
              ].map(f => (
                <button
                  key={f.id}
                  onClick={() => setFilterType(f.id)}
                  className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                    filterType === f.id
                      ? 'bg-slate-900 text-white'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            {dmUnreadTotal > 0 && (
              <Badge variant="destructive" className="text-sm px-3 py-1">{dmUnreadTotal} unread</Badge>
            )}
          </div>
        </div>

        {filteredThreads.length === 0 ? (
          <Card className="border-dashed p-12 text-center">
            <Inbox className="mx-auto mb-3 h-12 w-12 text-slate-300" />
            <p className="text-slate-500">No messages yet</p>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-[280px_1fr]">
            {/* ── Thread list ── */}
            <Card className="space-y-1 p-2 self-start">
              {filteredThreads.map((thread) => {
                const isSelected = selectedThreadId === thread.id;
                const isSentByAdmin = thread.senderRole === 'superadmin';
                return (
                  <div
                    key={thread.id}
                    className={`group relative flex items-start gap-1 rounded-xl px-3 py-3 transition-colors cursor-pointer ${
                      isSelected ? 'bg-slate-50 border border-slate-200' : 'hover:bg-slate-50'
                    }`}
                    onClick={() => setSelectedThreadId(thread.id)}
                  >
                    <div className="flex flex-1 items-center gap-2.5 min-w-0">
                      <div className="h-9 w-9 shrink-0 rounded-full bg-gradient-to-br from-slate-300 to-slate-400 flex items-center justify-center text-white text-sm font-bold">
                        {(thread.readerName || 'R')?.[0]?.toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold text-slate-900 truncate">{thread.readerName || 'Reader'}</p>
                          {thread.unreadByAdmin && (
                            <span className="flex h-2.5 w-2.5 rounded-full bg-red-500" />
                          )}
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Badge variant="outline" className="text-[9px] px-1.5 py-0">
                            {thread.messageType === 'author_related' ? 'Author' : 'General'}
                          </Badge>
                          {isSentByAdmin && (
                            <Badge variant="outline" className="text-[9px] px-1.5 py-0 border-teal-300 text-teal-700">
                              Sent by admin
                            </Badge>
                          )}
                          <p className="text-xs text-slate-400 truncate">{thread.lastMessage || ''}</p>
                        </div>
                      </div>
                    </div>
                    {/* Kebab menu on thread item */}
                    <div className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      <KebabMenu
                        onDeleteForMe={() => openDeleteModal(thread.id, 'forMe')}
                        onDeleteForEveryone={() => openDeleteModal(thread.id, 'forEveryone')}
                      />
                    </div>
                  </div>
                );
              })}
            </Card>

            {/* ── Message thread ── */}
            {selectedThreadId && activeThread ? (
              <Card className="flex flex-col overflow-hidden" style={{ height: '600px' }}>
                {/* Thread header with kebab */}
                <div className="border-b border-slate-100 px-4 py-3 bg-slate-50 flex items-center gap-3">
                  <div className="h-8 w-8 rounded-full bg-gradient-to-br from-slate-300 to-slate-400 flex items-center justify-center text-white text-sm font-bold">
                    {(activeThread.readerName || 'R')?.[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-slate-900 text-sm">{activeThread.readerName || 'Reader'}</p>
                      {activeThread.senderRole === 'superadmin' && (
                        <Badge variant="outline" className="text-[9px] px-1.5 py-0 border-teal-300 text-teal-700">
                          Sent by admin
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-400">
                      {activeThread.readerEmail}
                      {activeThread.messageType === 'author_related' && activeThread.relatedAuthorId && ' · Author-related'}
                      {activeThread.subject && ` · ${activeThread.subject}`}
                    </p>
                  </div>
                  <KebabMenu
                    onDeleteForMe={() => openDeleteModal(selectedThreadId, 'forMe')}
                    onDeleteForEveryone={() => openDeleteModal(selectedThreadId, 'forEveryone')}
                  />
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {threadMessages.map((msg) => {
                    const isAdmin = msg.sender === 'admin';
                    return (
                      <div key={msg.id} className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}>
                        {!isAdmin && (
                          <div className="mr-2 h-7 w-7 shrink-0 rounded-full bg-gradient-to-br from-slate-300 to-slate-400 flex items-center justify-center text-white text-xs font-bold">
                            {(activeThread.readerName || 'R')?.[0]?.toUpperCase()}
                          </div>
                        )}
                        <div className={`max-w-[70%] rounded-2xl px-4 py-2.5 ${
                          isAdmin ? 'rounded-br-sm bg-slate-500 text-white' : 'rounded-bl-sm bg-slate-100 text-slate-900'
                        }`}>
                          <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                          <p className={`mt-1 text-[10px] ${isAdmin ? 'text-slate-100' : 'text-slate-400'}`}>
                            {msg.createdAt?.toDate?.().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                            {' · '}
                            {msg.createdAt?.toDate?.().toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                          </p>
                        </div>
                        {isAdmin && (
                          <div className="ml-2 h-7 w-7 shrink-0 rounded-full bg-gradient-to-br from-slate-500 to-teal-600 flex items-center justify-center text-white text-xs font-bold">
                            J
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Reply bar */}
                <div className="border-t border-slate-100 p-3 flex gap-2">
                  <Input
                    type="text"
                    placeholder={`Reply to ${activeThread.readerName || 'reader'}...`}
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && replyText.trim()) handleReply(); }}
                    className="flex-1 rounded-xl"
                  />
                  <Button
                    onClick={handleReply}
                    disabled={!replyText.trim()}
                    size="icon"
                    className="rounded-xl"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </Card>
            ) : (
              <Card className="flex items-center justify-center border-dashed" style={{ height: '600px' }}>
                <p className="text-slate-400 text-sm">Select a conversation</p>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* ── Delete confirmation modal ── */}
      <Dialog open={deleteModal.open} onOpenChange={(v) => { if (!v) setDeleteModal({ open: false, threadId: null, mode: null }); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {deleteModal.mode === 'forMe' ? 'Delete for me only' : 'Delete for everyone'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {deleteModal.mode === 'forMe' ? (
              <p className="text-sm text-slate-600">
                This thread will be hidden from your view. Other admins will still be able to see it.
              </p>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-slate-600">
                  This will permanently delete the entire conversation, including all messages.
                </p>
                <p className="text-sm font-medium text-red-600">
                  Delete for everyone cannot be undone.
                </p>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteModal({ open: false, threadId: null, mode: null })}
            >
              Cancel
            </Button>
            <Button
              variant={deleteModal.mode === 'forEveryone' ? 'destructive' : 'default'}
              onClick={confirmDelete}
            >
              {deleteModal.mode === 'forMe' ? 'Hide thread' : 'Delete permanently'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Compose new message modal ── */}
      <Dialog open={composeOpen} onOpenChange={(v) => { if (!v) resetComposeModal(); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>New Message</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Recipient field */}
            {selectedRecipient ? (
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-700">To:</span>
                <Badge variant="outline" className="gap-1.5 px-2.5 py-1">
                  {selectedRecipient.type === 'author' ? (
                    <BookOpen className="h-3 w-3 text-teal-600" />
                  ) : (
                    <User className="h-3 w-3 text-slate-500" />
                  )}
                  {selectedRecipient.name}
                  <span className="text-slate-400">({selectedRecipient.email})</span>
                  <button
                    onClick={() => { setSelectedRecipient(null); setRecipientSearch(''); }}
                    className="ml-1 rounded-full p-0.5 hover:bg-slate-200"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-700">To:</span>
                  {/* Recipient type toggle */}
                  <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-0.5">
                    {[
                      { id: 'all', label: 'All' },
                      { id: 'authors', label: 'Authors' },
                      { id: 'readers', label: 'Readers' },
                    ].map(f => (
                      <button
                        key={f.id}
                        onClick={() => setRecipientFilter(f.id)}
                        className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                          recipientFilter === f.id
                            ? 'bg-slate-900 text-white'
                            : 'text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Search by name or email..."
                    value={recipientSearch}
                    onChange={(e) => setRecipientSearch(e.target.value)}
                    className="pl-9"
                  />
                  {/* Dropdown results */}
                  {recipientResults.length > 0 && (
                    <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-48 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
                      {recipientResults.map((r) => (
                        <button
                          key={`${r.type}-${r.id}`}
                          onClick={() => {
                            setSelectedRecipient(r);
                            setRecipientSearch('');
                            setRecipientResults([]);
                          }}
                          className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-slate-50 transition-colors"
                        >
                          <div className="h-7 w-7 shrink-0 rounded-full bg-gradient-to-br from-slate-300 to-slate-400 flex items-center justify-center text-white text-xs font-bold">
                            {r.name?.[0]?.toUpperCase() || '?'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">{r.name}</p>
                            <p className="text-xs text-slate-400 truncate">{r.email}</p>
                          </div>
                          <Badge
                            variant="outline"
                            className={`text-[9px] px-1.5 py-0 shrink-0 ${
                              r.type === 'author'
                                ? 'border-teal-300 text-teal-700'
                                : 'border-slate-300 text-slate-600'
                            }`}
                          >
                            {r.type === 'author' ? 'Author' : 'Reader'}
                          </Badge>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Subject (optional) */}
            <div>
              <Input
                placeholder="Subject (optional)"
                value={composeSubject}
                onChange={(e) => setComposeSubject(e.target.value)}
              />
            </div>

            {/* Message body */}
            <div>
              <Textarea
                placeholder="Write your message..."
                value={composeBody}
                onChange={(e) => setComposeBody(e.target.value)}
                className="min-h-[120px]"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={resetComposeModal}>
              Cancel
            </Button>
            <Button
              onClick={handleComposeSend}
              disabled={!selectedRecipient || !composeBody.trim() || composeSending}
              className="gap-1.5"
            >
              <Send className="h-4 w-4" />
              {composeSending ? 'Sending...' : 'Send'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
